import React, {Component} from 'react';
import cx from 'classnames';
import invoke from 'lodash.invoke';
import {PicklistContext} from './context';
import List from './List';
import Item from './Item';
import Selection from './Selection';
import EditAction from './EditAction';
import {t} from '../../util/i18n';
import {noop} from '@ultradent/utilities/Functions';

class PickList extends Component {

    static defaultProps = {
        list: [],
        itemIdProp: 'id',
        itemType: 'item',
        groupId: 'picklist',
        allowAddItem: false,
        defaultSelection: null,
        customProps: {}
    };

    static List = List;
    static Item = Item;
    static Selection = Selection;
    static EditAction = EditAction;

    constructor ( props ) {
        super( props );

        this.handleItemChange = this.handleItemChange.bind( this );
        this.handleItemAdded = this.handleItemAdded.bind( this );
        this.toggleEditing = this.toggleEditing.bind( this );
        this.toggleAddItem = this.toggleAddItem.bind( this );
        this.getToggleButtonLabel = this.getToggleButtonLabel.bind( this );
        this.isEditable = this.isEditable.bind( this );

        this.state = {
            allowAddItem: props.allowAddItem,
            selectedItem: this.getSelectedItem( props ),
            isEditing: false,
            isAddingItem: false
        };
    }

    getSelectedItem ( {defaultSelection, list} ) {
        // if we're dealing with a single item list this item should always be the selected item
        return (list.length === 1) ? list[0] : defaultSelection;
    }

    componentWillMount () {
        const renderProp = this.props.children;

        this.renderChildren = (typeof renderProp === 'function')
            ? renderProp
            : noop;
    }

    componentDidMount () {
        const {selectedItem} = this.state;
        if ( selectedItem != null ) {
            invoke( this.props, 'onItemChange', selectedItem, this.props.groupId );
        }
    }

    componentDidUpdate ( previousProps, previousState, previousContext ) {
        const {list, itemIdProp, defaultSelection} = this.props;

        if ( !previousProps.defaultSelection || !defaultSelection ) {
            return;
        }

        if ( previousProps.defaultSelection[itemIdProp] !== defaultSelection[itemIdProp] ) {
            const selectedItem = list.find( item => String( defaultSelection[itemIdProp] ) ===
                String( item[itemIdProp] ) );
            this.setState( {selectedItem} );
        }
    }

    isEditable () {
        return this.props.list.length > 1 || this.state.allowAddItem;
    }

    handleItemChange ( id ) {
        const {list, itemIdProp, groupId} = this.props;
        const selectedItem = list.find( item => String( id ) === String( item[itemIdProp] ) );

        this.toggleEditing();
        this.setState( {selectedItem} );
        invoke( this.props, 'onItemChange', selectedItem, groupId );
    }

    handleItemAdded ( newItem, ...rest ) {
        this.setState( {
            selectedItem: newItem,
            isAddingItem: false,
            isEditing: false
        } );
        invoke( this.props, 'onItemAdded', newItem, ...rest );
    }

    toggleEditing () {
        this.setState( {
            isEditing: !this.state.isEditing,
            isAddingItem: false
        } );

        // return scroll to selected item
        if ( this.container ) {
            this.container.focus();
        }
    }

    toggleAddItem () {
        this.setState( {isAddingItem: !this.state.isAddingItem} )
    }

    getToggleButtonLabel () {
        const {isEditing, selectedItem} = this.state;

        if ( isEditing ) {
            return t( 'pickList.actionLabel.cancel', 'Cancel change' );
        }
        if ( !selectedItem ) {
            return t( 'pickList.actionLabel.select', 'Select item', {itemType: this.props.itemType} );
        }

        return t( 'pickList.actionLabel.change', 'Change' );
    }

    getContext () {
        const {list, itemIdProp, itemType, groupId, allowAddItem, defaultSelection, customProps} = this.props;
        return {
            ...this.state,
            list, itemIdProp, itemType, groupId, allowAddItem, defaultSelection,
            ...customProps,
            getToggleButtonLabel: this.getToggleButtonLabel,
            isEditable: this.isEditable,
            actions: {
                handleItemChange: this.handleItemChange,
                handleItemAdded: this.handleItemAdded,
                toggleEditing: this.toggleEditing,
                toggleAddItem: this.toggleAddItem
            }
        }
    }

    render () {
        const {className} = this.props;
        const pickList = this.getContext();
        const containerClasses = cx( className, 'relative' );

        return (
            <PicklistContext.Provider value={pickList}>
                <div className={containerClasses} tabIndex="-1" ref={ref => this.container = ref}>
                    {this.renderChildren( pickList )}
                </div>
            </PicklistContext.Provider>
        );
    }
}

export default PickList;
